import * as React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { PanelMarker } from '../../models';

/**
 * The mark on a marker whose bands are the practitioner's OWN reference range
 * ("My Ranges") rather than LabSmarts'.
 *
 * A DOT, not an asterisk: the asterisk already means "previous collection
 * only" on the bar-graph panel, and two asterisks meaning two things in one
 * table is worse than no mark at all. Brand brown rather than a status color,
 * because it sits beside charts whose whole vocabulary is red / yellow /
 * green and a mark in any of those reads as a finding about the client.
 *
 * Deliberately quiet, for a reason that is easy to miss: the analysis screen
 * is NOT a practitioner-only surface. Practitioners share it with the client
 * in a consult, so the mark has to be answerable on hover without being a
 * question on sight. A labeled "Custom range" chip fails that test.
 *
 * SCREEN ONLY. Callers must not render it into a PDF. The bands in the
 * client's report move with the practitioner's ranges; the report never says
 * whose they are. That is the standing exception to PDF parity and it is
 * deliberate: a client has never been shown the lab's own optimal range to
 * compare against, so labeling one band as customized would raise a question
 * that does not otherwise exist, about the marker the practitioner thought
 * hardest about.
 *
 * One component for both report screens, so the two can never end up saying
 * different things about the same range.
 */
export function MyRangeDot(props: { panelMarker: PanelMarker }) {
  const pm = props.panelMarker;
  const applied = pm && pm.range && pm.range.appliedRangeOverride;
  if (!applied) {
    return null;
  }
  const bound = (value?: number) =>
    pm.displayBound ? pm.displayBound(value) : value == null ? '' : `${value}`;
  const changed = applied.changedDate
    ? moment(applied.changedDate).format('MM/DD/YYYY')
    : '';
  // Optimal, not all six: it is the band every finding in the report is
  // judged against, and a six-number tooltip is a table, not an answer.
  const tooltip = (
    <Tooltip id={`my-range-${pm.attribute}`}>
      <div className="ref-range-my-range-tooltip">
        <div>
          Your range: optimal {bound(pm.range.optimalMin)} –{' '}
          {bound(pm.range.optimalMax)}
        </div>
        <div>
          LabSmarts&rsquo;: {bound(applied.labSmarts.optimalMin)} –{' '}
          {bound(applied.labSmarts.optimalMax)}
        </div>
        {changed ? <div>Changed {changed}</div> : null}
      </div>
    </Tooltip>
  );
  return (
    <OverlayTrigger overlay={tooltip} placement="top">
      <span className="ref-range-my-range-dot" aria-label="Your range">
        &bull;
      </span>
    </OverlayTrigger>
  );
}

/** Whether this marker's bands are the practitioner's own. */
export function isMyRange(pm?: PanelMarker): boolean {
  return !!(pm && pm.range && pm.range.appliedRangeOverride);
}

/**
 * "Ferritin is graded on your ranges." / "Ferritin and Glucose are graded on
 * your ranges."
 *
 * Named rather than left to the dots, because a practitioner reviewing a
 * report before an appointment should not have to scan six bars for a mark.
 */
export function myRangeNote(names: string[]): string {
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return `${names[0]} is graded on your ranges.`;
  }
  return `${names.slice(0, -1).join(', ')} and ${
    names[names.length - 1]
  } are graded on your ranges.`;
}
